import { Button, TextField, useToast } from "@stagvista/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import * as React from "react";

import { AuthLayout } from "../components/auth-layout.js";
import { useCloudSession } from "../hooks/use-cloud-session.js";
import { CloudApiError } from "../lib/cloud-api-client.js";

export const Route = createFileRoute("/sign-in")({
  component: SignInPage,
});

function SignInPage(): React.ReactElement {
  const { signIn } = useCloudSession();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      void navigate({ to: "/" });
    } catch (err) {
      const message =
        err instanceof CloudApiError
          ? (err.problem.detail ?? err.problem.title)
          : "Sign-in failed.";
      setError(message);
      toast({ tone: "error", title: "Sign-in failed", description: message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back — sign in to manage your services.">
      <form className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error ?? undefined}
        />
        <Button type="submit" loading={submitting} className="mt-2">
          Sign in
        </Button>
      </form>
      <Link
        to="/forgot-password"
        className="mt-4 block text-center text-caption text-brand-primary"
      >
        Forgot password?
      </Link>
    </AuthLayout>
  );
}
